import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { doc, updateDoc, increment, setDoc } from "firebase/firestore"; // Import Firestore functions
import { db } from '../firebase-config'; // Ensure this path is correct
import './AdCard.css';

const AdCard = ({ adData, isActive }) => {

  // Helper to determine the location label (Same logic as your NewsCard)
  const getLocationLabel = () => {
    const type = adData.targetPostTypes?.[0]; // Get the first targeted type
    if (type === 'Andhra Pradesh') return 'AP';
    if (type === 'Telangana') return 'TS';
    if (type === 'World') return 'World';
    if (type === 'India') return 'India';
    return '';
  };
  const [showFullMedia, setShowFullMedia] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const videoRef = useRef(null);
    const cardRef = useRef(null); // Ref for the whole card to track views
    const hasRecordedView = useRef(false); // Prevent multiple counts in one session
  
    // A unified way to check if the ad contains video media
    const videoSrc = adData?.videoUrl || adData?.uploadedImageUrl;
  const isVideo = adData?.mediaType === 'video' || (videoSrc && (videoSrc.includes('.mp4') || videoSrc.includes('video')));
  
  const images = adData?.imageUrls?.length > 0 
    ? adData.imageUrls 
    : (adData?.uploadedImageUrls?.length > 0 ? adData.uploadedImageUrls : [adData?.imageUrl].filter(Boolean));

  // --- NEW: AD VIEW TRACKING LOGIC ---
  useEffect(() => {
    if (!adData.id || hasRecordedView.current) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(async (entry) => {
        if (entry.isIntersecting) {
          console.log("AD VISIBLE:", adData.title); // DEBUG LOG 1
          
          // Wait 1.5 seconds to ensure user is actually looking at it
          const timer = setTimeout(async () => {
            if (!hasRecordedView.current) {
              try {
                const adRef = doc(db, "advertisements", adData.id);
                
                // Use { merge: true } via updateDoc to ensure it works even if field is missing
                await updateDoc(adRef, { 
                  viewCount: increment(1) 
                });
                
                hasRecordedView.current = true;
                console.log("VIEW RECORDED SUCCESS for:", adData.title); // DEBUG LOG 2
              } catch (err) {
                console.error("VIEW RECORD ERROR:", err); // DEBUG LOG 3
              }
            }
          }, 1500);

          return () => clearTimeout(timer);
        }
      });
    }, { 
      threshold: 0.2, // Lowered threshold to 20%
      // rootMargin: '0px' 
    });

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
}, [adData.id]);

  // 2. Play/Pause state based directly on slider isActive status
   // --- REVISED: Play unmuted automatically when slide is active ---
  useEffect(() => {
    if (!isVideo || !videoRef.current || !videoSrc) return;

    if (isActive) {
      // 1. Explicitly unmute video programmatically
      videoRef.current.muted = false;

      const timer = setTimeout(() => {
        if (videoRef.current) {
          // 2. Play with sound
          videoRef.current.play().catch((unmutedPlayError) => {
            console.warn("Unmuted autoplay blocked by OS. Falling back to muted autoplay...", unmutedPlayError);
            
            // Fail-safe fallback to prevent loading freezes
            if (videoRef.current) {
              videoRef.current.muted = true;
              videoRef.current.play().catch((mutedPlayError) => {
                console.error("Muted playback fallback also failed:", mutedPlayError);
              });
            }
          });
        }
      }, 50);
      return () => clearTimeout(timer);
    } else {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    }
  }, [isActive, isVideo, videoSrc]);

    const handleOpenMedia = (url) => {
    setSelectedMedia(url);
    setShowFullMedia(true);
  };

  const renderMedia = () => {
    if (isVideo) {
      return (
        <video 
            ref={videoRef}
            key={videoSrc} 
            src={videoSrc} 
            className="news-card-image"
            controls 
            playsInline 
            // muted 
            loop 
            preload={isActive ? "auto" : "none"}
            onClick={(e) => e.stopPropagation()} 
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', backgroundColor: '#000' }}
        />
      );
    }

  // IMAGE CODE REMAINS UNCHANGED AS REQUESTED
  const count = images.length;
  if (count === 0) return <div className="ad-media-placeholder">Sponsored Content</div>;
  if (count === 1) {
  return (
    <img 
      src={images[0]} 
      className="news-card-image ad-main-media" 
      alt="ad" 
      onClick={() => handleOpenMedia(images[0])} 
      style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#000' }} 
      decoding="async"
      loading={isActive ? "eager" : "lazy"} // Load instantly if active, lazy load otherwise
    />
  );
  }
  return (
    <div className={`ad-grid-layout grid-${count > 4 ? 4 : count}`}>
      {images.slice(0, 4).map((url, i) => (
        <div key={i} className="ad-grid-cell" onClick={() => handleOpenMedia(url)}>
          <img 
          src={url} 
          alt="ad" 
          className="ad-main-media" 
          decoding="async"
          loading={isActive ? "eager" : "lazy"} // Keeps multi-grid images deferred until needed
        />
          {i === 3 && count > 4 && <div className="grid-overlay">+{count - 4}</div>}
        </div>
      ))}
    </div>
  );
};

  return (
    <div className="news-card ad-card-container" ref={cardRef}>
      {/* Portals for full screen remains same... */}
      {showFullMedia && ReactDOM.createPortal(
        <div className="full-screen-image-portal" onClick={() => setShowFullMedia(false)}>
            <div className="portal-content">
                {isVideo ? (
                    <video src={selectedMedia} controls autoPlay className="portal-img" onClick={(e) => e.stopPropagation()} />
                ) : (
                    <img src={selectedMedia} alt="Full view" className="portal-img" />
                )}
                <div className="portal-close">✕</div>
            </div>
        </div>,
        document.body
      )}

      <div className="news-image-container">
        {renderMedia()}
        <span className="ad-tag-overlay">Sponsored</span>
      </div>

      <div className="news-card-content-wrapper" style={{ padding: '15px' }}>
        <div className="news-author-header">
            <span className="author-name">{adData.sponsor}</span>
            <span className="post-relative-time">Ad</span>
        </div>
        <h3 className="news-card-title">{adData.title}</h3>
        {adData.link && (
          <a href={adData.link} target="_blank" rel="noopener noreferrer" className="ad-open-now-btn">
            OPEN NOW
          </a>
        )}
      </div>
    </div>
  );
};

export default AdCard;